// ============================================================================
// CLEO'S SECRET — CleoScheduler
// File: src/pages/CleoScheduler.jsx
//
// Four-step booking wizard plus a confirmation state.
//
// Pattern: TRANSACTIONAL surface, distinct from editorial/dashboard/conversation.
//   • SAND header strip with Cinzel "STEP N OF 4" indicator
//   • Single-column narrow form (560px), tight step-to-step flow
//   • Selection cards with 3px gold left bar when selected (matching the
//     Stage 2 focus pattern from MyResults markers)
//   • Date/time grids with the same selection treatment
//   • Editorial confirmation: italic premise + summary + portal CTA
//
// Preserved logic:
//   • /api/save-booking endpoint and payload unchanged
//   • All four step state machines unchanged
//   • Weekday-only date generation (10 days)
//   • 9am-4pm time slots, half-hours, excluding 12-1 lunch
//   • Email guard before save
//   • saved / saving / error state transitions
// ============================================================================

import React, { useState } from 'react';
import {
  FF, FS, P,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';

// ───────── Booking options ─────────
var CONSULTATION_TYPES = [
  { id: 'initial', label: 'Initial consultation', duration: 60, fee: 280, description: 'Comprehensive first appointment with your GP.' },
  { id: 'followup', label: 'Follow-up consultation', duration: 30, fee: 180, description: 'Review and refine your care plan.' },
  { id: 'results', label: 'Results review', duration: 45, fee: 220, description: 'Detailed walkthrough of your investigations.' },
];

var PRACTITIONERS = [
  { id: 'robyn', name: "Dr Robyn O'Sullivan", role: "GP, Women's health lead", available: true },
  { id: 'team', name: 'Next available GP', role: "Cleo's Secret medical team", available: true },
];

// ───────── Time slot generation ─────────
function generateTimeSlots() {
  var slots = [];
  for (var h = 9; h <= 16; h++) {
    if (h !== 12 && h !== 13) {
      slots.push({
        time: h + ':00',
        display: (h > 12 ? h - 12 : h) + ':00 ' + (h >= 12 ? 'PM' : 'AM'),
      });
      if (h !== 16) {
        slots.push({
          time: h + ':30',
          display: (h > 12 ? h - 12 : h) + ':30 ' + (h >= 12 ? 'PM' : 'AM'),
        });
      }
    }
  }
  return slots;
}

function getNextWeekdays(count) {
  var days = [];
  var today = new Date();
  var i = 1;
  while (days.length < count) {
    var d = new Date(today);
    d.setDate(today.getDate() + i);
    if (d.getDay() !== 0 && d.getDay() !== 6) days.push(d);
    i++;
  }
  return days;
}

function formatDateLong(date) {
  return date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });
}

function dateForApi(date) {
  var y = date.getFullYear();
  var m = String(date.getMonth() + 1).padStart(2, '0');
  var d = String(date.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + d;
}

// ───────── Step label (the small Cinzel marker above each step body) ─────────
function StepLabel({ n, total, children }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 14,
      marginBottom: 'clamp(18px, 2.2vw, 26px)',
      paddingBottom: 'clamp(12px, 1.4vw, 16px)',
      borderBottom: '1px solid ' + P.SAND3,
    }}>
      <span style={{
        fontFamily: FF,
        fontSize: 'clamp(11px, 0.95vw, 13px)',
        fontWeight: 700,
        letterSpacing: '0.32em',
        color: P.GO,
      }}>{String(n).padStart(2, '0')}</span>
      <span style={{
        fontFamily: FF,
        fontSize: 'clamp(11px, 0.95vw, 13px)',
        fontWeight: 700,
        letterSpacing: '0.32em',
        textTransform: 'uppercase',
        color: P.BR2,
      }}>{children}</span>
      <span style={{
        marginLeft: 'auto',
        fontFamily: FF,
        fontSize: 'clamp(10px, 0.85vw, 12px)',
        fontWeight: 700,
        letterSpacing: '0.28em',
        color: P.BR2,
        textTransform: 'uppercase',
      }}>Step {n} of {total}</span>
    </div>
  );
}

// ───────── Selectable card (used for type + practitioner) ─────────
function SelectCard({ selected, onClick, children }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: 'clamp(20px, 2.2vw, 26px)',
        marginBottom: 12,
        background: selected ? 'rgba(201, 169, 97, 0.08)' : P.PAP,
        border: '1px solid ' + (selected ? P.GO : P.SAND3),
        borderLeft: '3px solid ' + (selected ? P.GO : P.SAND3),
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
      onMouseEnter={function(e) {
        if (!selected) e.currentTarget.style.background = 'rgba(232,221,196,0.4)';
      }}
      onMouseLeave={function(e) {
        if (!selected) e.currentTarget.style.background = P.PAP;
      }}>
      {children}
    </div>
  );
}

// ───────── Primary action button (Cinzel uppercase, INK→BR hover) ─────────
function PrimaryBtn({ children, onClick, disabled, flex }) {
  var active = !disabled;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: flex,
        width: flex ? undefined : '100%',
        fontFamily: FS,
        fontSize: 'clamp(11px, 0.95vw, 12px)',
        fontWeight: 600,
        letterSpacing: '0.28em',
        textTransform: 'uppercase',
        padding: '16px 28px',
        background: active ? P.INK : 'rgba(26,15,8,0.3)',
        color: P.PAP,
        border: 'none',
        cursor: active ? 'pointer' : 'default',
        transition: 'background 0.2s',
      }}
      onMouseEnter={function(e) { if (active) e.currentTarget.style.background = P.BR; }}
      onMouseLeave={function(e) { if (active) e.currentTarget.style.background = P.INK; }}>
      {children}
    </button>
  );
}

// ───────── Ghost back button ─────────
function GhostBtn({ children, onClick, flex }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: flex,
        width: flex ? undefined : '100%',
        fontFamily: FS,
        fontSize: 'clamp(11px, 0.95vw, 12px)',
        fontWeight: 600,
        letterSpacing: '0.28em',
        textTransform: 'uppercase',
        padding: '16px 28px',
        background: 'transparent',
        color: P.BR2,
        border: '1px solid ' + P.GO,
        cursor: 'pointer',
        transition: 'background 0.2s',
      }}
      onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(201, 169, 97, 0.08)'; }}
      onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}>
      {children}
    </button>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// CleoScheduler
// ═════════════════════════════════════════════════════════════════════════
export default function CleoScheduler(props) {
  var onNavigate = props.onNavigate;
  var email = props.email;

  var stepState = useState(1);
  var step = stepState[0];
  var setStep = stepState[1];

  var typeState = useState(null);
  var selectedType = typeState[0];
  var setSelectedType = typeState[1];

  var practState = useState(null);
  var selectedPract = practState[0];
  var setSelectedPract = practState[1];

  var dateState = useState(null);
  var selectedDate = dateState[0];
  var setSelectedDate = dateState[1];

  var timeState = useState(null);
  var selectedTime = timeState[0];
  var setSelectedTime = timeState[1];

  var notesState = useState('');
  var notes = notesState[0];
  var setNotes = notesState[1];

  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  var savedState = useState(false);
  var saved = savedState[0];
  var setSaved = savedState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var days = getNextWeekdays(10);
  var times = selectedDate ? generateTimeSlots() : [];

  function confirmBooking() {
    if (!email) {
      setError('Please complete your health conversation first so we have your email.');
      return;
    }

    setSaving(true);
    setError(null);

    fetch('/api/save-booking', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email,
        consultationType: selectedType.id,
        consultationLabel: selectedType.label,
        practitionerId: selectedPract.id,
        practitionerName: selectedPract.name,
        appointmentDate: dateForApi(selectedDate),
        appointmentTime: selectedTime.time,
        durationMinutes: selectedType.duration,
        feeAmount: selectedType.fee,
        patientNotes: notes || null,
      }),
    })
      .then(function(res) {
        if (!res.ok) {
          return res.json().then(function(err) { throw new Error(err.error || 'Could not save booking'); });
        }
        return res.json();
      })
      .then(function() { setSaved(true); setSaving(false); })
      .catch(function(err) { setError(err.message); setSaving(false); });
  }

  // ════════════════════════════════════════════════════════════════════════
  // CONFIRMATION STATE
  // ════════════════════════════════════════════════════════════════════════
  if (saved) {
    return (
      <div style={{
        fontFamily: FF,
        background: P.PAP,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(40px, 6vw, 80px) clamp(24px, 3vw, 40px)',
      }}>
        <div style={{
          maxWidth: 600,
          width: '100%',
          textAlign: 'center',
        }}>
          {/* Ankh seal — canonical page-opener for confirmation state */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 'clamp(28px, 3vw, 40px)',
          }}>
            <Ankh responsive="closing" color={P.GO} />
          </div>

          {/* Eyebrow */}
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.36em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 18,
          }}>Confirmed</div>

          {/* Title */}
          <h1 style={{
            fontFamily: FF,
            fontSize: 'clamp(32px, 4vw, 56px)',
            fontWeight: 400,
            color: P.INK,
            lineHeight: 1.1,
            letterSpacing: '-0.015em',
            margin: '0 0 clamp(18px, 2vw, 24px)',
          }}>Your <span style={{ fontStyle: 'italic', color: P.BR2 }}>appointment</span> is booked.</h1>

          {/* Gold accent rule */}
          <div style={{
            width: 'clamp(140px, 14vw, 180px)',
            height: 1,
            background: P.GO,
            margin: '0 auto clamp(28px, 3vw, 36px)',
          }} />

          {/* Booking summary */}
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(18px, 1.6vw, 22px)',
            color: P.TX,
            lineHeight: 1.55,
            marginBottom: 8,
          }}>{selectedType.label} with {selectedPract.name}.</p>

          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(18px, 1.6vw, 22px)',
            fontStyle: 'italic',
            color: P.BR2,
            lineHeight: 1.55,
            marginBottom: 'clamp(32px, 3.5vw, 44px)',
          }}>{formatDateLong(selectedDate)} at {selectedTime.display}.</p>

          {/* Confirmation note */}
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(15px, 1.3vw, 17px)',
            color: P.TL,
            lineHeight: 1.85,
            maxWidth: 480,
            margin: '0 auto clamp(32px, 3.5vw, 48px)',
          }}>
            A confirmation has been saved to your records. Your GP will receive your intake summary before the appointment.
          </p>

          {/* CTA */}
          <button
            onClick={function() { if (onNavigate) onNavigate('portal'); }}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '16px 40px',
              background: P.INK,
              color: P.PAP,
              border: 'none',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={function(e) { e.currentTarget.style.background = P.BR; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = P.INK; }}>
            View your portal
          </button>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // MAIN WIZARD
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ fontFamily: FF, background: P.PAP, minHeight: '100vh' }}>

      {/* ═════════ Header strip ═════════ */}
      <header style={{
        background: P.SAND,
        borderBottom: '1px solid ' + P.SAND3,
        padding: 'clamp(28px, 3.4vw, 44px) clamp(20px, 3vw, 40px) clamp(20px, 2.4vw, 28px)',
        textAlign: 'center',
      }}>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(10px, 0.9vw, 12px)',
          fontWeight: 700,
          letterSpacing: '0.36em',
          textTransform: 'uppercase',
          color: P.BR2,
          marginBottom: 10,
        }}>Cleo's Secret</div>
        <h1 style={{
          fontFamily: FF,
          fontSize: 'clamp(26px, 3vw, 38px)',
          fontWeight: 400,
          color: P.INK,
          lineHeight: 1.15,
          letterSpacing: '-0.012em',
          margin: 0,
        }}>Book your <span style={{ fontStyle: 'italic', color: P.BR2 }}>consultation.</span></h1>

        {/* Progress meter — 4 segments, gold fill */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 6,
          marginTop: 'clamp(18px, 2vw, 22px)',
        }}>
          {[1, 2, 3, 4].map(function(n) {
            return (
              <div key={n} style={{
                width: step >= n ? 36 : 14,
                height: 2,
                background: step >= n ? P.GO : P.SAND3,
                transition: 'all 0.4s ease',
              }} />
            );
          })}
        </div>
      </header>

      {/* ═════════ Wizard body ═════════ */}
      <main style={{
        maxWidth: 640,
        margin: '0 auto',
        padding: 'clamp(32px, 4vw, 56px) clamp(20px, 3vw, 40px) clamp(48px, 6vw, 80px)',
      }}>

        {/* ─── STEP 1: Consultation type ─── */}
        {step === 1 && (
          <div>
            <StepLabel n={1} total={4}>Choose consultation type</StepLabel>

            {CONSULTATION_TYPES.map(function(type) {
              var isSelected = selectedType && selectedType.id === type.id;
              return (
                <SelectCard
                  key={type.id}
                  selected={isSelected}
                  onClick={function() { setSelectedType(type); }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: 16,
                    marginBottom: 8,
                  }}>
                    <h3 style={{
                      fontFamily: FF,
                      fontSize: 'clamp(18px, 1.6vw, 22px)',
                      fontWeight: 500,
                      color: P.INK,
                      lineHeight: 1.2,
                      letterSpacing: '-0.005em',
                      margin: 0,
                    }}>{type.label}</h3>
                    <span style={{
                      fontFamily: FF,
                      fontSize: 'clamp(18px, 1.6vw, 22px)',
                      fontWeight: 500,
                      color: P.BR2,
                      whiteSpace: 'nowrap',
                      letterSpacing: '-0.005em',
                    }}>${type.fee}</span>
                  </div>
                  <p style={{
                    fontFamily: FF,
                    fontSize: 'clamp(14px, 1.2vw, 15px)',
                    color: P.TL,
                    lineHeight: 1.65,
                    marginBottom: 10,
                  }}>{type.description}</p>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(10px, 0.85vw, 11px)',
                    fontWeight: 700,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: P.BR2,
                  }}>{type.duration} minutes</div>
                </SelectCard>
              );
            })}

            <div style={{ marginTop: 'clamp(20px, 2.4vw, 28px)' }}>
              <PrimaryBtn
                onClick={function() { if (selectedType) setStep(2); }}
                disabled={!selectedType}>
                Continue
              </PrimaryBtn>
            </div>
          </div>
        )}

        {/* ─── STEP 2: Practitioner ─── */}
        {step === 2 && (
          <div>
            <StepLabel n={2} total={4}>Choose your practitioner</StepLabel>

            {PRACTITIONERS.map(function(p) {
              var isSelected = selectedPract && selectedPract.id === p.id;
              return (
                <SelectCard
                  key={p.id}
                  selected={isSelected}
                  onClick={function() { setSelectedPract(p); }}>
                  <h3 style={{
                    fontFamily: FF,
                    fontSize: 'clamp(18px, 1.6vw, 22px)',
                    fontWeight: 500,
                    color: P.INK,
                    lineHeight: 1.2,
                    letterSpacing: '-0.005em',
                    margin: '0 0 8px',
                  }}>{p.name}</h3>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(14px, 1.2vw, 15px)',
                    fontStyle: 'italic',
                    color: P.BR2,
                    lineHeight: 1.5,
                  }}>{p.role}</div>
                </SelectCard>
              );
            })}

            <div style={{
              display: 'flex',
              gap: 10,
              marginTop: 'clamp(20px, 2.4vw, 28px)',
            }}>
              <GhostBtn onClick={function() { setStep(1); }} flex={1}>Back</GhostBtn>
              <PrimaryBtn
                onClick={function() { if (selectedPract) setStep(3); }}
                disabled={!selectedPract}
                flex={2}>
                Continue
              </PrimaryBtn>
            </div>
          </div>
        )}

        {/* ─── STEP 3: Date and time ─── */}
        {step === 3 && (
          <div>
            <StepLabel n={3} total={4}>Choose date and time</StepLabel>

            {/* Date grid */}
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(10px, 0.85vw, 11px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: P.BR2,
              marginBottom: 12,
            }}>Available dates</div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
              gap: 8,
              marginBottom: 'clamp(24px, 2.8vw, 32px)',
            }}>
              {days.map(function(d, i) {
                var isSel = selectedDate && selectedDate.toDateString() === d.toDateString();
                return (
                  <div
                    key={i}
                    onClick={function() { setSelectedDate(d); setSelectedTime(null); }}
                    style={{
                      padding: 'clamp(10px, 1.1vw, 14px) 8px',
                      background: isSel ? 'rgba(201, 169, 97, 0.08)' : P.PAP,
                      border: '1px solid ' + (isSel ? P.GO : P.SAND3),
                      borderTop: '3px solid ' + (isSel ? P.GO : P.SAND3),
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={function(e) {
                      if (!isSel) e.currentTarget.style.background = 'rgba(232,221,196,0.4)';
                    }}
                    onMouseLeave={function(e) {
                      if (!isSel) e.currentTarget.style.background = P.PAP;
                    }}>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(9px, 0.75vw, 10px)',
                      fontWeight: 700,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: P.BR2,
                      marginBottom: 4,
                    }}>{d.toLocaleDateString('en-AU', { weekday: 'short' })}</div>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(22px, 2.2vw, 28px)',
                      fontWeight: 400,
                      color: P.INK,
                      lineHeight: 1,
                      letterSpacing: '-0.015em',
                    }}>{d.getDate()}</div>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(9px, 0.75vw, 10px)',
                      fontWeight: 700,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: P.BR2,
                      marginTop: 4,
                    }}>{d.toLocaleDateString('en-AU', { month: 'short' })}</div>
                  </div>
                );
              })}
            </div>

            {/* Time grid */}
            {selectedDate && (
              <div style={{ marginBottom: 'clamp(24px, 2.8vw, 32px)' }}>
                <div style={{
                  fontFamily: FF,
                  fontSize: 'clamp(10px, 0.85vw, 11px)',
                  fontWeight: 700,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  color: P.BR2,
                  marginBottom: 6,
                }}>Available times</div>
                <div style={{
                  fontFamily: FF,
                  fontSize: 'clamp(13px, 1.1vw, 15px)',
                  fontStyle: 'italic',
                  color: P.BR2,
                  marginBottom: 14,
                }}>{formatDateLong(selectedDate)}</div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
                  gap: 8,
                }}>
                  {times.map(function(t) {
                    var isSel = selectedTime && selectedTime.time === t.time;
                    return (
                      <div
                        key={t.time}
                        onClick={function() { setSelectedTime(t); }}
                        style={{
                          padding: '12px 8px',
                          background: isSel ? 'rgba(201, 169, 97, 0.08)' : P.PAP,
                          border: '1px solid ' + (isSel ? P.GO : P.SAND3),
                          cursor: 'pointer',
                          textAlign: 'center',
                          fontFamily: FF,
                          fontSize: 'clamp(13px, 1.1vw, 14px)',
                          fontWeight: 500,
                          color: P.INK,
                          letterSpacing: '0.02em',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={function(e) {
                          if (!isSel) e.currentTarget.style.background = 'rgba(232,221,196,0.4)';
                        }}
                        onMouseLeave={function(e) {
                          if (!isSel) e.currentTarget.style.background = P.PAP;
                        }}>
                        {t.display}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: 10,
              marginTop: 'clamp(20px, 2.4vw, 28px)',
            }}>
              <GhostBtn onClick={function() { setStep(2); }} flex={1}>Back</GhostBtn>
              <PrimaryBtn
                onClick={function() { if (selectedDate && selectedTime) setStep(4); }}
                disabled={!selectedDate || !selectedTime}
                flex={2}>
                Continue
              </PrimaryBtn>
            </div>
          </div>
        )}

        {/* ─── STEP 4: Review and confirm ─── */}
        {step === 4 && (
          <div>
            <StepLabel n={4} total={4}>Review and confirm</StepLabel>

            {/* Summary panel — three rows */}
            <div style={{
              background: P.PAP,
              border: '1px solid ' + P.SAND3,
              borderLeft: '3px solid ' + P.GO,
              padding: 'clamp(24px, 2.8vw, 32px)',
              marginBottom: 'clamp(20px, 2.4vw, 28px)',
            }}>
              <SummaryRow
                label="Consultation"
                primary={selectedType.label}
                secondary={selectedType.duration + ' minutes \u00b7 $' + selectedType.fee}
              />
              <div style={{
                height: 1,
                background: P.SAND3,
                margin: 'clamp(16px, 1.8vw, 22px) 0',
              }} />
              <SummaryRow
                label="Practitioner"
                primary={selectedPract.name}
                secondary={selectedPract.role}
              />
              <div style={{
                height: 1,
                background: P.SAND3,
                margin: 'clamp(16px, 1.8vw, 22px) 0',
              }} />
              <SummaryRow
                label="Date and time"
                primary={formatDateLong(selectedDate)}
                secondary={selectedTime.display}
              />
            </div>

            {/* Notes field */}
            <div style={{ marginBottom: 'clamp(20px, 2.4vw, 28px)' }}>
              <label style={{
                display: 'block',
                fontFamily: FF,
                fontSize: 'clamp(10px, 0.85vw, 11px)',
                fontWeight: 700,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: P.BR2,
                marginBottom: 8,
              }}>Anything else to share <span style={{ fontFamily: FF, fontStyle: 'italic', fontWeight: 400, textTransform: 'none', letterSpacing: '0.02em', color: P.TF, fontSize: '0.95em' }}>(optional)</span></label>
              <textarea
                value={notes}
                onChange={function(e) { setNotes(e.target.value); }}
                placeholder={'Specific questions or concerns for your appointment\u2026'}
                rows={3}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  fontFamily: FF,
                  fontSize: 'clamp(14px, 1.2vw, 16px)',
                  color: P.INK,
                  background: P.PAP,
                  border: '1px solid ' + P.SAND3,
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  lineHeight: 1.65,
                  transition: 'border-color 0.2s',
                }}
                onFocus={function(e) { e.currentTarget.style.borderColor = P.BR; }}
                onBlur={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}
              />
            </div>

            {/* Error */}
            {error && (
              <div style={{
                padding: 'clamp(14px, 1.6vw, 18px) clamp(16px, 1.8vw, 22px)',
                background: P.RED_BG,
                border: '1px solid ' + P.RED,
                borderLeft: '3px solid ' + P.RED,
                marginBottom: 'clamp(18px, 2vw, 24px)',
                fontFamily: FF,
                fontSize: 'clamp(13px, 1.1vw, 15px)',
                color: P.RED,
                fontStyle: 'italic',
                lineHeight: 1.6,
              }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <GhostBtn onClick={function() { setStep(3); }} flex={1}>Back</GhostBtn>
              <PrimaryBtn
                onClick={confirmBooking}
                disabled={saving}
                flex={2}>
                {saving ? 'Confirming\u2026' : 'Confirm booking'}
              </PrimaryBtn>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

// ───────── SummaryRow — reusable row in the review panel ─────────
function SummaryRow({ label, primary, secondary }) {
  return (
    <div>
      <div style={{
        fontFamily: FF,
        fontSize: 'clamp(10px, 0.85vw, 11px)',
        fontWeight: 700,
        letterSpacing: '0.28em',
        textTransform: 'uppercase',
        color: P.BR2,
        marginBottom: 8,
      }}>{label}</div>
      <div style={{
        fontFamily: FF,
        fontSize: 'clamp(17px, 1.5vw, 20px)',
        fontWeight: 500,
        color: P.INK,
        lineHeight: 1.25,
        letterSpacing: '-0.005em',
        marginBottom: 4,
      }}>{primary}</div>
      {secondary && (
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(13px, 1.1vw, 15px)',
          fontStyle: 'italic',
          color: P.BR2,
          lineHeight: 1.5,
        }}>{secondary}</div>
      )}
    </div>
  );
}
